/*
 * LensTrove lens configurator
 *
 * Two cart lines per submission, linked by _bundle_id:
 *   1) Frame variant (color-keyed product)
 *   2) Lens add-on variant from one of:
 *        lens-add-on-non-prescription   (Feature × Thickness)
 *        lens-add-on-prescription       (Feature × Thickness, w/ Rx)
 *        lens-add-on-reading            (Reading Strength × Feature)
 *
 * Steps by lens type:
 *   Non-Prescription : Frame → Lens Type → Features → Thickness → Review
 *   Single Vision    : Frame → Lens Type → Prescription → Features → Thickness → Review
 *   Readers          : Frame → Lens Type → Prescription → Features → Review
 */

(function () {
  const root = document.querySelector('[data-lens-configurator]');
  if (!root) return;

  const form = root.querySelector('form[data-lens-form]');
  const stepsEl = root.querySelector('[data-steps]');
  const panels = root.querySelectorAll('[data-panel]');
  const productData = JSON.parse(root.querySelector('[data-product-json]').textContent);
  const LENS_TYPES = JSON.parse(root.querySelector('[data-lens-types]').textContent);
  const FEATURES = JSON.parse(root.querySelector('[data-lens-features]').textContent);
  const THICKNESSES = JSON.parse(root.querySelector('[data-lens-thicknesses]').textContent);
  const LENS_ADDONS = JSON.parse(root.querySelector('[data-lens-addons]').textContent);

  const state = {
    stepIndex: 0,
    flow: ['Lens Type'],
    frameColor: productData.initialColor || productData.frameColors[0] || '',
    lensTypeId: null,
    featureId: null,
    thicknessId: null,
    rx: {},
    rxFile: null,
    rxFileUrl: null,
    rxMode: 'manual',
    editing: null
  };

  const MAX_RX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

  function flowForType(id) {
    if (!id) return ['Lens Type'];
    const lt = LENS_TYPES.find(l => l.id === id);
    if (!lt) return ['Lens Type'];
    const steps = ['Lens Type'];
    if (lt.needsRx) steps.push('Prescription');
    steps.push('Features');
    if (lt.hasThickness) steps.push('Thickness');
    steps.push('Review');
    return steps;
  }

  function findFrameVariantId(color) {
    if (!productData.variants || !productData.variants.length) return productData.defaultVariantId;
    const target = (color || '').trim().toLowerCase();
    const byOption = productData.variants.find(v =>
      (v.options || []).some(o => (o || '').toLowerCase() === target)
    );
    if (byOption) return byOption.id;
    const byTitle = productData.variants.find(v => (v.title || '').toLowerCase() === target);
    return (byTitle && byTitle.id) || productData.defaultVariantId;
  }

  function findLensAddonVariant() {
    const lt = LENS_TYPES.find(l => l.id === state.lensTypeId);
    if (!lt) return null;
    const list = LENS_ADDONS[lt.addonHandle] || [];
    if (!list.length) return null;
    const feature = FEATURES.find(f => f.id === state.featureId);
    if (!feature) return null;

    if (state.lensTypeId === 'readers') {
      const add = state.rx.add && state.rx.add.value;
      if (!add) return null;
      const target = '+' + parseFloat(add).toFixed(2);
      return list.find(v =>
        (v.options || []).indexOf(target) !== -1 &&
        (v.options || []).indexOf(feature.featureOption) !== -1
      );
    }

    const thickness = THICKNESSES.find(t => t.id === state.thicknessId);
    if (!thickness) return null;
    return list.find(v =>
      (v.options || []).indexOf(feature.featureOption) !== -1 &&
      (v.options || []).indexOf(thickness.thicknessOption) !== -1
    );
  }

  function dollarsFmt(n) { return '$' + n.toFixed(2); }
  function fmtSigned(v) { const f = parseFloat(v); return (f >= 0 ? '+' : '') + f.toFixed(2); }

  function lensPrice() {
    const variant = findLensAddonVariant();
    if (variant && typeof variant.price === 'number') return variant.price / 100;
    if (variant && typeof variant.price === 'string') return parseFloat(variant.price);
    const lt = LENS_TYPES.find(l => l.id === state.lensTypeId);
    return lt ? lt.price : 0;
  }
  function totalPrice() { return productData.framePrice + lensPrice(); }

  function renderSteps() {
    state.flow = flowForType(state.lensTypeId);
    stepsEl.innerHTML = state.flow.map((label, i) => {
      const isPast = i < state.stepIndex;
      const isCurr = i === state.stepIndex;
      return `<div class="lt-step ${isPast ? 'is-past' : ''} ${isCurr ? 'is-current' : ''}">${isPast ? '✓ ' : ''}${label}</div>`;
    }).join('');
  }

  function showPanel(name) {
    panels.forEach(p => { p.hidden = p.getAttribute('data-panel') !== name; });
  }

  function gotoStep(index) {
    state.flow = flowForType(state.lensTypeId);
    state.stepIndex = Math.max(0, Math.min(state.flow.length - 1, index));
    const label = state.flow[state.stepIndex];
    renderSteps();
    showPanel(label);
    renderPanel(label);
    const back = root.querySelector('[data-drawer-back]');
    if (back) back.hidden = state.stepIndex === 0;
    const title = root.querySelector('[data-drawer-title]');
    if (title) title.textContent = label === 'Review' ? 'Review your order' : 'Choose your lenses';
    const drawerBody = root.querySelector('.lt-drawer__body');
    if (drawerBody) drawerBody.scrollTop = 0;
  }

  function updateTotalsUI() {
    root.querySelectorAll('[data-total]').forEach(el => el.textContent = dollarsFmt(totalPrice()));
    root.querySelectorAll('[data-lens-price]').forEach(el => el.textContent = dollarsFmt(lensPrice()));
    root.querySelectorAll('[data-frame-price]').forEach(el => el.textContent = dollarsFmt(productData.framePrice));
  }

  const COLOR_HEX = {
    'black':'#111111','white':'#F5F5F5','crystal':'linear-gradient(135deg,#F0F0F0 0%,#D8D8D8 100%)','clear':'linear-gradient(135deg,#F0F0F0 0%,#D8D8D8 100%)',
    'tortoise':'linear-gradient(135deg,#7A4A1E 0%,#C8893B 60%,#3B2010 100%)','wood tortoise':'linear-gradient(135deg,#5C3A1E 0%,#A87545 60%,#2E1A0A 100%)',
    'gold':'#D4AF37','rose gold':'#B76E79','silver':'#C0C0C0','gunmetal':'#3E4548','matte black':'#1C1C1C','blue':'#2C5FBD','navy':'#1B2A55',
    'red':'#C8232C','wine':'#6E1B2B','burgundy':'#6E1B2B','pink':'#F2A6B4','blush':'#F1C5C0','coral':'#F4614A','green':'#2F6A52','sage':'#B4C6A6',
    'honey':'#C99A52','brown':'#6B4423','grey':'#8A8A8A','gray':'#8A8A8A','champagne':'#E3C89E','nude':'#E3C89E','lemon':'#FFE135','yellow':'#FDD835','purple':'#6B4A9A'
  };

  function resolveColor(name) {
    const key = (name || '').trim().toLowerCase();
    if (COLOR_HEX[key]) return COLOR_HEX[key];
    const parts = key.split(/\s+/).filter(Boolean);
    const hexes = parts.map(p => COLOR_HEX[p]).filter(Boolean);
    if (hexes.length >= 2) return `linear-gradient(135deg, ${hexes[0]} 0%, ${hexes[0]} 48%, ${hexes[1]} 52%, ${hexes[1]} 100%)`;
    if (hexes.length === 1) return hexes[0];
    return '#D0D0D0';
  }

  function buildColorPicker() {
    const colorPicker = root.querySelector('[data-color-picker]');
    if (!colorPicker || colorPicker.dataset.built) return;
    colorPicker.innerHTML = productData.frameColors.map((c, i) => {
      const selected = productData.initialColor ? (c === productData.initialColor) : (i === 0);
      const bg = resolveColor(c);
      return `<button type="button" class="lt-swatch${selected ? ' is-active' : ''}" data-color="${c}" title="${c}" aria-label="${c}"><span class="lt-swatch__dot" style="background: ${bg};"></span></button>`;
    }).join('');
    colorPicker.addEventListener('click', e => {
      const btn = e.target.closest('[data-color]');
      if (!btn) return;
      state.frameColor = btn.dataset.color;
      colorPicker.querySelectorAll('[data-color]').forEach(b => b.classList.toggle('is-active', b === btn));
      root.querySelectorAll('[data-selected-color]').forEach(el => { el.textContent = state.frameColor; });
      syncDrawerMedia();
    });
    colorPicker.dataset.built = '1';
  }

  function openDrawer() {
    const drawer = root.querySelector('[data-lens-drawer]');
    if (!drawer) return;
    syncDrawerMedia();
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lt-drawer-open');
  }

  function syncDrawerMedia() {
    const target = root.querySelector('[data-drawer-media]');
    if (!target) return;
    // Find a thumbnail on the PDP whose data-color matches the selection; reuse its image
    const match = root.querySelector('.lt-pdp__thumb[data-color="' + cssEscape(state.frameColor) + '"]');
    const main = root.querySelector('[data-media-main] img');
    const fallback = main && main.src;
    const src = (match && match.getAttribute('data-full')) || fallback;
    if (src) target.src = src;
    target.alt = productData.title + ' in ' + state.frameColor;
  }
  function cssEscape(s) {
    return String(s || '').replace(/(["\\])/g, '\\$1');
  }
  function closeDrawer() {
    const drawer = root.querySelector('[data-lens-drawer]');
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lt-drawer-open');
  }

  function renderPanel(label) {
    if (label === 'Lens Type') {
      const list = root.querySelector('[data-lens-type-list]');
      if (list && !list.dataset.built) {
        list.innerHTML = LENS_TYPES.map(lt => `
          <button type="button" class="lt-choice ${state.lensTypeId === lt.id ? 'is-active' : ''}" data-lens-type="${lt.id}">
            <span class="lt-choice__icon">${lt.icon}</span>
            <span class="lt-choice__body">
              <span class="lt-choice__label">${lt.label}</span>
              <span class="lt-choice__sub">${lt.sublabel}</span>
            </span>
            <span class="lt-choice__price">From ${dollarsFmt(lt.price)}</span>
          </button>`).join('');
        list.addEventListener('click', e => {
          const btn = e.target.closest('[data-lens-type]');
          if (!btn) return;
          if (state.lensTypeId !== btn.dataset.lensType) {
            state.lensTypeId = btn.dataset.lensType;
            state.featureId = null;
            state.thicknessId = null;
            state.rx = {};
            state.rxFile = null;
            state.rxFileUrl = null;
            state.rxMode = 'manual';
          }
          list.querySelectorAll('[data-lens-type]').forEach(b => b.classList.toggle('is-active', b === btn));
          updateContinueButtons();
          updateTotalsUI();
        });
        list.dataset.built = '1';
      } else if (list) {
        // Re-sync active state on revisit (e.g. after edit prefill or back navigation)
        list.querySelectorAll('[data-lens-type]').forEach(b => b.classList.toggle('is-active', b.dataset.lensType === state.lensTypeId));
      }
      updateContinueButtons();
    }
    if (label === 'Prescription') {
      const host = root.querySelector('[data-rx-host]');
      if (host) {
        host.innerHTML = state.lensTypeId === 'readers' ? rxReadersHTML() : rxSingleVisionHTML();
        wireRx(host);
      }
      const title = root.querySelector('[data-rx-title]');
      const desc = root.querySelector('[data-rx-desc]');
      if (state.lensTypeId === 'readers') {
        title.textContent = 'Choose reading power';
        desc.textContent = 'Select the ADD power from your prescription or reading glasses.';
      } else {
        title.textContent = 'Enter your prescription';
        desc.textContent = 'Enter exactly as written on your prescription. SPH is required; CYL and Axis only if you have astigmatism.';
      }
      wireUpload();
      renderUpload();
      syncRxModeUI();
      updateContinueButtons();
    }
    if (label === 'Features') {
      const list = root.querySelector('[data-feature-list]');
      list.innerHTML = FEATURES.map(f => `
        <button type="button" class="lt-choice ${state.featureId === f.id ? 'is-active' : ''}" data-feature="${f.id}">
          <span class="lt-choice__icon">${f.icon}</span>
          <span class="lt-choice__body">
            <span class="lt-choice__label-row">
              <span class="lt-choice__label">${f.label}</span>
              ${f.recommended ? '<span class="lt-pop">Popular</span>' : ''}
            </span>
            <span class="lt-choice__sub">${f.sublabel}</span>
          </span>
          <span class="lt-choice__price">${f.upcharge === 0 ? 'Included' : '+$' + f.upcharge.toFixed(2)}</span>
        </button>`).join('');
      if (!list.dataset.built) {
        list.addEventListener('click', e => {
          const btn = e.target.closest('[data-feature]');
          if (!btn) return;
          state.featureId = btn.dataset.feature;
          list.querySelectorAll('[data-feature]').forEach(b => b.classList.toggle('is-active', b === btn));
          updateContinueButtons();
          updateTotalsUI();
        });
        list.dataset.built = '1';
      }
      updateContinueButtons();
    }
    if (label === 'Thickness') {
      const list = root.querySelector('[data-thickness-list]');
      list.innerHTML = THICKNESSES.map(t => {
        // Compute incremental upcharge vs Standard for THIS feature in the matrix
        const sampleCents = sampleThicknessCents(t.thicknessOption);
        const standardCents = sampleThicknessCents('Standard 1.57');
        const deltaCents = (sampleCents !== null && standardCents !== null) ? (sampleCents - standardCents) : 0;
        const priceLabel = deltaCents <= 0 ? 'Included' : '+$' + (deltaCents / 100).toFixed(2);
        return `
        <button type="button" class="lt-choice ${state.thicknessId === t.id ? 'is-active' : ''}" data-thickness="${t.id}">
          <span class="lt-choice__body">
            <span class="lt-choice__label-row">
              <span class="lt-choice__label">${t.label}</span>
              ${t.recommended ? '<span class="lt-pop">Popular</span>' : ''}
            </span>
            <span class="lt-choice__sub">${t.sublabel}</span>
          </span>
          <span class="lt-choice__price">${priceLabel}</span>
        </button>`;
      }).join('');
      if (!list.dataset.built) {
        list.addEventListener('click', e => {
          const btn = e.target.closest('[data-thickness]');
          if (!btn) return;
          state.thicknessId = btn.dataset.thickness;
          list.querySelectorAll('[data-thickness]').forEach(b => b.classList.toggle('is-active', b === btn));
          updateContinueButtons();
          updateTotalsUI();
        });
        list.dataset.built = '1';
      }
      updateContinueButtons();
    }
    if (label === 'Review') renderReview();
    updateTotalsUI();
  }

  function sampleThicknessCents(thicknessOption) {
    const lt = LENS_TYPES.find(l => l.id === state.lensTypeId);
    if (!lt) return null;
    const list = LENS_ADDONS[lt.addonHandle] || [];
    const feature = FEATURES.find(f => f.id === state.featureId);
    if (!feature) return null;
    const v = list.find(x =>
      (x.options || []).indexOf(feature.featureOption) !== -1 &&
      (x.options || []).indexOf(thicknessOption) !== -1
    );
    if (!v) return null;
    if (typeof v.price === 'number') return v.price;
    if (typeof v.price === 'string') return Math.round(parseFloat(v.price) * 100);
    return null;
  }

  function rxSingleVisionHTML() {
    // SPH: natural order -10.00 → 0.00 → +10.00, default selected at 0.00
    const sphOpts = range(-10, 10, 0.25);
    state.rx.od = state.rx.od || {};
    state.rx.os = state.rx.os || {};
    if (!state.rx.od.sph) state.rx.od.sph = '0.00';
    if (!state.rx.os.sph) state.rx.os.sph = '0.00';
    if (state.rx.od.cyl === undefined) state.rx.od.cyl = '0.00';
    if (state.rx.os.cyl === undefined) state.rx.os.cyl = '0.00';
    // CYL: 0.00, -0.25, -0.50, ... -6.00 (closest-to-zero first)
    const cylOpts = range(-6, 0, 0.25).filter(v => v !== 0).reverse();
    const axisOpts = range(1, 180, 1);
    const pdOpts = range(55, 75, 0.5);
    const pdHalfOpts = range(25, 40, 0.5);

    function sel(name, values, fmt, current) {
      return `<select data-rx="${name}">
        <option value="">Select</option>
        ${values.map(v => {
          const display = fmt(v);
          const raw = typeof v === 'number' ? v.toFixed(2) : v;
          const selected = current === raw ? 'selected' : '';
          return `<option value="${raw}" ${selected}>${display}</option>`;
        }).join('')}
      </select>`;
    }

    const dual = !!(state.rx.pd && state.rx.pd.dual);

    return `
      <div class="lt-rx-grid">
        ${['od', 'os'].map((eye, i) => {
          const label = i === 0 ? 'OD (Right)' : 'OS (Left)';
          const hasCyl = state.rx[eye] && state.rx[eye].cyl && state.rx[eye].cyl !== '0.00';
          return `
            <div>
              <div class="lt-eye-label">${label}</div>
              <div class="lt-field">
                <label>Sphere (SPH)</label>
                ${sel(eye + '.sph', sphOpts, v => fmtSigned(v), (state.rx[eye] || {}).sph)}
              </div>
              <div class="lt-field">
                <label>Cylinder (CYL)</label>
                <select data-rx="${eye}.cyl">
                  <option value="">Select</option>
                  <option value="0.00" ${(state.rx[eye] || {}).cyl === '0.00' ? 'selected' : ''}>0.00 (no astigmatism)</option>
                  ${cylOpts.map(v => `<option value="${v.toFixed(2)}" ${(state.rx[eye] || {}).cyl === v.toFixed(2) ? 'selected' : ''}>${fmtSigned(v)}</option>`).join('')}
                </select>
              </div>
              ${hasCyl ? `
                <div class="lt-field">
                  <label>Axis</label>
                  <select data-rx="${eye}.axis">
                    <option value="">Select</option>
                    ${axisOpts.map(v => `<option value="${v}" ${(state.rx[eye] || {}).axis === String(v) ? 'selected' : ''}>${v}°</option>`).join('')}
                  </select>
                </div>` : ''}
            </div>`;
        }).join('')}
      </div>
      <div class="lt-pd">
        <div class="lt-pd__head">
          <label>Pupillary Distance (PD)</label>
          <button type="button" data-pd-toggle>${dual ? 'Use single PD' : 'Use dual PD'}</button>
        </div>
        ${dual ? `
          <div class="lt-pd__dual">
            ${['right', 'left'].map(side => `
              <div class="lt-field">
                <label>${side} eye</label>
                <select data-rx="pd.${side}">
                  <option value="">Select</option>
                  ${pdHalfOpts.map(v => `<option value="${v.toFixed(1)}" ${(state.rx.pd || {})[side] === v.toFixed(1) ? 'selected' : ''}>${v.toFixed(1)} mm</option>`).join('')}
                </select>
              </div>`).join('')}
          </div>` : `
          <select data-rx="pd.single">
            <option value="">Select</option>
            ${pdOpts.map(v => `<option value="${v.toFixed(1)}" ${(state.rx.pd || {}).single === v.toFixed(1) ? 'selected' : ''}>${v.toFixed(1)} mm</option>`).join('')}
          </select>`}
        <div class="lt-pd__hint">💡 Find your PD on your prescription or measure it yourself.</div>
      </div>`;
  }

  function rxReadersHTML() {
    const addOpts = range(1.0, 3.5, 0.25);
    const pdOpts = range(55, 75, 0.5);
    return `
      <div>
        <div class="lt-eye-label">Reading Power (ADD)</div>
        <div class="lt-add-grid">
          ${addOpts.map(v => `
            <button type="button" class="lt-add-btn ${((state.rx.add || {}).value === v.toFixed(2)) ? 'is-active' : ''}" data-rx="add.value" data-value="${v.toFixed(2)}">
              +${v.toFixed(2)}
            </button>`).join('')}
        </div>
        <div class="lt-pd__hint">💡 Common values are +1.00 to +2.50.</div>
      </div>
      <div class="lt-pd">
        <label>Pupillary Distance (PD)</label>
        <select data-rx="pd.single">
          <option value="">Select PD</option>
          ${pdOpts.map(v => `<option value="${v.toFixed(1)}" ${(state.rx.pd || {}).single === v.toFixed(1) ? 'selected' : ''}>${v.toFixed(1)} mm</option>`).join('')}
        </select>
      </div>`;
  }

  function wireRx(host) {
    host.addEventListener('change', e => {
      const el = e.target.closest('[data-rx]');
      if (!el) return;
      const [section, field] = el.getAttribute('data-rx').split('.');
      state.rx[section] = state.rx[section] || {};
      state.rx[section][field] = el.value;
      if (field === 'cyl' || field === 'dual') {
        const h = root.querySelector('[data-rx-host]');
        h.innerHTML = state.lensTypeId === 'readers' ? rxReadersHTML() : rxSingleVisionHTML();
        wireRx(h);
      }
      updateContinueButtons();
    });
    host.addEventListener('click', e => {
      const addBtn = e.target.closest('.lt-add-btn');
      if (addBtn) {
        state.rx.add = { value: addBtn.dataset.value };
        host.querySelectorAll('.lt-add-btn').forEach(b => b.classList.toggle('is-active', b === addBtn));
        updateContinueButtons();
        updateTotalsUI();
        return;
      }
      const pdToggle = e.target.closest('[data-pd-toggle]');
      if (pdToggle) {
        state.rx.pd = state.rx.pd || {};
        state.rx.pd.dual = !state.rx.pd.dual;
        const h = root.querySelector('[data-rx-host]');
        h.innerHTML = state.lensTypeId === 'readers' ? rxReadersHTML() : rxSingleVisionHTML();
        wireRx(h);
        updateContinueButtons();
      }
    });
  }

  function rxComplete() {
    const lt = LENS_TYPES.find(l => l.id === state.lensTypeId);
    if (!lt || !lt.needsRx) return true;
    if (state.rxMode === 'upload') return !!(state.rxFile || state.rxFileUrl);
    if (state.lensTypeId === 'readers') {
      return !!(state.rx.add && state.rx.add.value && state.rx.pd && state.rx.pd.single);
    }
    const od = state.rx.od || {}, os = state.rx.os || {}, pd = state.rx.pd || {};
    const odOk = od.sph && od.cyl !== undefined && (od.cyl === '0.00' || od.axis);
    const osOk = os.sph && os.cyl !== undefined && (os.cyl === '0.00' || os.axis);
    const pdOk = pd.single || (pd.right && pd.left);
    return odOk && osOk && pdOk;
  }

  function syncRxModeUI() {
    const mode = state.rxMode || 'manual';
    root.querySelectorAll('[data-rx-mode]').forEach(b => {
      b.classList.toggle('is-active', b.dataset.rxMode === mode);
    });
    root.querySelectorAll('[data-rx-section]').forEach(sec => {
      sec.hidden = sec.dataset.rxSection !== mode;
    });
  }

  function setRxMode(mode) {
    if (mode !== 'manual' && mode !== 'upload') return;
    state.rxMode = mode;
    if (mode === 'manual') {
      state.rxFile = null;
      state.rxFileUrl = null;
      const input = root.querySelector('[data-rx-upload-input]');
      if (input) input.value = '';
      renderUpload();
    } else {
      state.rx = {};
    }
    syncRxModeUI();
    if (mode === 'manual') {
      const host = root.querySelector('[data-rx-host]');
      if (host) {
        host.innerHTML = state.lensTypeId === 'readers' ? rxReadersHTML() : rxSingleVisionHTML();
        wireRx(host);
      }
    }
    updateContinueButtons();
    updateTotalsUI();
  }

  function wireUpload() {
    const wrap = root.querySelector('[data-rx-upload]');
    if (!wrap || wrap.dataset.built) return;
    wrap.dataset.built = '1';
    const input = wrap.querySelector('[data-rx-upload-input]');
    const trigger = wrap.querySelector('[data-rx-upload-trigger]');
    const remove = wrap.querySelector('[data-rx-upload-remove]');

    trigger.addEventListener('click', () => input.click());
    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      const errEl = wrap.querySelector('[data-rx-upload-error]');
      errEl.hidden = true; errEl.textContent = '';
      if (!file) return;
      if (file.size > MAX_RX_FILE_BYTES) {
        errEl.textContent = 'File too large — max 10 MB.';
        errEl.hidden = false;
        input.value = '';
        return;
      }
      state.rxFile = file;
      renderUpload();
      updateContinueButtons();
    });
    remove.addEventListener('click', () => {
      state.rxFile = null;
      state.rxFileUrl = null;
      input.value = '';
      renderUpload();
      updateContinueButtons();
    });
  }

  function renderUpload() {
    const wrap = root.querySelector('[data-rx-upload]');
    if (!wrap) return;
    const hint = wrap.querySelector('[data-rx-upload-hint]');
    const fileEl = wrap.querySelector('[data-rx-upload-file]');
    const filename = wrap.querySelector('[data-rx-upload-filename]');
    if (state.rxFile) {
      hint.hidden = true;
      fileEl.hidden = false;
      filename.textContent = state.rxFile.name;
    } else if (state.rxFileUrl) {
      hint.hidden = true;
      fileEl.hidden = false;
      filename.textContent = state.rxFileUrl.split('/').pop();
    } else {
      hint.hidden = false;
      fileEl.hidden = true;
      filename.textContent = '';
    }
  }

  function updateContinueButtons() {
    const current = state.flow[state.stepIndex];
    let canProceed = true;
    if (current === 'Lens Type') canProceed = !!state.lensTypeId;
    if (current === 'Prescription') canProceed = rxComplete();
    if (current === 'Features') canProceed = !!state.featureId;
    if (current === 'Thickness') canProceed = !!state.thicknessId;
    const cont = root.querySelector('.lt-panel:not([hidden]) [data-continue]');
    if (cont) cont.classList.toggle('is-disabled', !canProceed);
  }

  function renderReview() {
    const lt = LENS_TYPES.find(l => l.id === state.lensTypeId);
    const f = FEATURES.find(x => x.id === state.featureId);
    const t = THICKNESSES.find(x => x.id === state.thicknessId);
    const host = root.querySelector('[data-review]');
    const lensVariant = findLensAddonVariant();

    let rxSummary = '';
    if (lt && lt.needsRx) {
      if (state.rxMode === 'upload' && state.rxFile) {
        rxSummary = `<div class="lt-review__rx">
          <div class="lt-review__rx-head">Prescription</div>
          <div>📎 ${state.rxFile.name} — we'll enter your Rx values from this file.</div>
        </div>`;
      } else if (state.lensTypeId === 'readers') {
        rxSummary = `<div class="lt-review__rx">
          <div class="lt-review__rx-head">Prescription</div>
          <div>ADD: +${state.rx.add.value} · PD: ${state.rx.pd.single} mm</div>
        </div>`;
      } else {
        const od = state.rx.od || {}, os = state.rx.os || {}, pd = state.rx.pd || {};
        rxSummary = `<div class="lt-review__rx">
          <div class="lt-review__rx-head">Prescription</div>
          <div>OD: SPH ${od.sph ? fmtSigned(od.sph) : '–'} · CYL ${od.cyl || '–'}${od.axis ? ' · Axis ' + od.axis + '°' : ''}</div>
          <div>OS: SPH ${os.sph ? fmtSigned(os.sph) : '–'} · CYL ${os.cyl || '–'}${os.axis ? ' · Axis ' + os.axis + '°' : ''}</div>
          <div>PD: ${pd.single ? pd.single + ' mm' : 'R ' + (pd.right || '–') + ' / L ' + (pd.left || '–') + ' mm'}</div>
        </div>`;
      }
    }

    host.innerHTML = `
      <div class="lt-review__row">
        <div>
          <div class="lt-review__row-label">Frame</div>
          <div class="lt-review__row-value">${productData.title} · ${state.frameColor}</div>
        </div>
        <span class="lt-mono">${dollarsFmt(productData.framePrice)}</span>
      </div>
      <div class="lt-review__row">
        <div>
          <div class="lt-review__row-label">Lens type</div>
          <div class="lt-review__row-value">${lt ? lt.label : ''}</div>
          ${f ? `<div class="lt-review__row-sub">${f.label}${t ? ' · ' + t.label : ''}</div>` : ''}
        </div>
        <span class="lt-mono">${dollarsFmt(lensPrice())}</span>
      </div>
      ${rxSummary}
      <div class="lt-review__total">
        <span class="lt-display" style="font-size:16px;color:#fff;">Total</span>
        <span class="lt-mono" style="font-size:24px;color:var(--lt-lemon);" data-total>${dollarsFmt(totalPrice())}</span>
      </div>`;
  }

  function buildBundle() {
    const lt = LENS_TYPES.find(l => l.id === state.lensTypeId);
    if (!lt) return null;
    const lensVariant = findLensAddonVariant();
    if (!lensVariant) return null;

    const feature = FEATURES.find(x => x.id === state.featureId);
    const thickness = THICKNESSES.find(x => x.id === state.thicknessId);
    const frameVariantId = findFrameVariantId(state.frameColor);
    const bundleId = 'b_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

    const frameProps = {
      '_bundle_id': bundleId,
      '_bundle_role': 'frame',
      'Frame color': state.frameColor,
      'Lens type': lt.label,
      'Lens feature': feature ? feature.label : ''
    };
    if (thickness && lt.hasThickness) frameProps['Lens thickness'] = thickness.label + ' (' + thickness.sublabel + ')';

    const lensProps = {
      '_bundle_id': bundleId,
      '_bundle_role': 'lens',
      'For frame': productData.title + ' · ' + state.frameColor
    };
    if (lt.needsRx) {
      if (state.rxMode === 'upload' && (state.rxFile || state.rxFileUrl)) {
        lensProps['Prescription source'] = 'Customer-uploaded file (we\'ll enter values)';
        if (state.rxFileUrl && !state.rxFile) lensProps['Prescription file'] = state.rxFileUrl;
      } else if (state.lensTypeId === 'readers') {
        lensProps['ADD power'] = '+' + state.rx.add.value;
        lensProps['PD'] = state.rx.pd.single + ' mm';
      } else {
        const od = state.rx.od || {}, os = state.rx.os || {}, pd = state.rx.pd || {};
        lensProps['OD (Right)'] = 'SPH ' + fmtSigned(od.sph) + ' · CYL ' + (od.cyl || '–') + (od.axis ? ' · Axis ' + od.axis + '°' : '');
        lensProps['OS (Left)']  = 'SPH ' + fmtSigned(os.sph) + ' · CYL ' + (os.cyl || '–') + (os.axis ? ' · Axis ' + os.axis + '°' : '');
        lensProps['PD'] = pd.single ? (pd.single + ' mm') : ('R ' + pd.right + ' / L ' + pd.left + ' mm');
      }
    }

    return {
      items: [
        { id: frameVariantId, quantity: 1, properties: frameProps },
        { id: lensVariant.id, quantity: 1, properties: lensProps }
      ],
      lensRxFile: (state.rxMode === 'upload') ? (state.rxFile || null) : null
    };
  }

  form.addEventListener('submit', e => {
    e.preventDefault();
    const bundle = buildBundle();
    const submitBtn = form.querySelector('[data-submit]');
    if (!bundle) {
      submitBtn.classList.remove('is-disabled');
      submitBtn.textContent = 'Missing details — please review';
      return;
    }
    submitBtn.classList.add('is-disabled');
    submitBtn.textContent = 'Adding…';

    const file = bundle.lensRxFile;
    let request;
    if (file) {
      const fd = new FormData();
      bundle.items.forEach((it, i) => {
        fd.append('items[' + i + '][id]', String(it.id));
        fd.append('items[' + i + '][quantity]', String(it.quantity));
        Object.entries(it.properties).forEach(([k, v]) => {
          fd.append('items[' + i + '][properties][' + k + ']', v == null ? '' : String(v));
        });
      });
      // Attach the file to the lens line (item index 1) under the "Prescription file" property
      fd.append('items[1][properties][Prescription file]', file, file.name);
      request = fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: fd
      });
    } else {
      const payload = { items: bundle.items };
      request = fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    request
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(data.description || data.message || 'Add to cart failed'), { data });
        return data;
      })
      .then(async () => {
        // If editing an existing bundle, remove the old frame+lens lines after the new ones are added
        if (state.editing) {
          const updates = {};
          updates[state.editing.frameKey] = 0;
          updates[state.editing.lensKey] = 0;
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates })
          }).catch(() => {});
        }
        submitBtn.classList.remove('is-disabled');
        submitBtn.innerHTML = state.editing ? '✓ Updated — going to bag…' : '✓ Added — going to bag…';
        return fetch('/cart.js').then(r => r.json()).then(cart => {
          document.querySelectorAll('.lt-cart-bubble').forEach(el => { el.textContent = visibleCartCount(cart); });
          window.dispatchEvent(new CustomEvent('lenstrove:cart:updated', { detail: cart }));
        });
      })
      .then(() => {
        window.location.href = '/cart';
      })
      .catch(err => {
        submitBtn.classList.remove('is-disabled');
        submitBtn.textContent = 'Error: ' + (err && err.message ? err.message : 'try again');
        console.error('[lens-configurator]', err);
      });
  });

  root.addEventListener('click', e => {
    const modeTab = e.target.closest('[data-rx-mode]');
    if (modeTab) { setRxMode(modeTab.dataset.rxMode); return; }
    const close = e.target.closest('[data-close-drawer]');
    if (close) { closeDrawer(); return; }
    const drawerBack = e.target.closest('[data-drawer-back]');
    if (drawerBack) { gotoStep(state.stepIndex - 1); return; }
    const startLenses = e.target.closest('[data-start-lenses]');
    if (startLenses) {
      gotoStep(0);
      openDrawer();
      return;
    }
    const next = e.target.closest('[data-continue]');
    const back = e.target.closest('[data-back]');
    if (next && !next.classList.contains('is-disabled')) gotoStep(state.stepIndex + 1);
    if (back) gotoStep(state.stepIndex - 1);
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      const drawer = root.querySelector('[data-lens-drawer]');
      if (drawer && drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
    }
  });

  buildColorPicker();
  renderSteps();
  showPanel(state.flow[0]);
  renderPanel(state.flow[0]);
  updateTotalsUI();

  // If we arrived with ?edit_bundle=…, prefill state from the matching cart bundle.
  (function maybeLoadEdit() {
    const params = new URLSearchParams(window.location.search);
    const bundleId = params.get('edit_bundle');
    if (!bundleId) return;
    fetch('/cart.js').then(r => r.json()).then(cart => {
      const items = cart.items || [];
      const frameLine = items.find(i => (i.properties || {})._bundle_id === bundleId && (i.properties || {})._bundle_role === 'frame');
      const lensLine  = items.find(i => (i.properties || {})._bundle_id === bundleId && (i.properties || {})._bundle_role === 'lens');
      if (!frameLine || !lensLine) return;
      // Only prefill if this PDP matches the edited frame product
      if (String(frameLine.product_id) !== String(productData.id)) return;

      state.editing = { frameKey: frameLine.key, lensKey: lensLine.key, bundleId };

      // Frame color
      const fc = (frameLine.properties || {})['Frame color'];
      if (fc) state.frameColor = fc;

      // Lens type
      const lensTypeLabel = (frameLine.properties || {})['Lens type'];
      if (lensTypeLabel) {
        const lt = LENS_TYPES.find(x => x.label === lensTypeLabel);
        if (lt) state.lensTypeId = lt.id;
      }

      // Feature
      const featureLabel = (frameLine.properties || {})['Lens feature'];
      if (featureLabel) {
        const f = FEATURES.find(x => x.label === featureLabel);
        if (f) state.featureId = f.id;
      }

      // Thickness ("Thin (1.61 index)" → match by label OR sublabel)
      const thicknessLabel = (frameLine.properties || {})['Lens thickness'];
      if (thicknessLabel) {
        const t = THICKNESSES.find(x => thicknessLabel.indexOf(x.label) === 0 || thicknessLabel.indexOf(x.sublabel) !== -1);
        if (t) state.thicknessId = t.id;
      }

      // Rx — uploaded file
      const rxFileUrl = (lensLine.properties || {})['Prescription file'];
      if (rxFileUrl) {
        state.rxMode = 'upload';
        state.rxFileUrl = rxFileUrl;
      } else {
        // Manual Rx — parse OD / OS / PD / ADD
        const props = lensLine.properties || {};
        if (props['ADD power']) {
          state.rx.add = { value: String(props['ADD power']).replace('+', '') };
          if (props['PD']) state.rx.pd = { single: String(props['PD']).replace(' mm', '') };
        } else {
          state.rx.od = parseEyeProp(props['OD (Right)']);
          state.rx.os = parseEyeProp(props['OS (Left)']);
          if (props['PD']) {
            const pdRaw = String(props['PD']);
            if (pdRaw.indexOf('R ') === 0) {
              const m = pdRaw.match(/R\s+([\d.]+)\s*\/\s*L\s+([\d.]+)/);
              if (m) state.rx.pd = { dual: true, right: m[1], left: m[2] };
            } else {
              state.rx.pd = { single: pdRaw.replace(' mm', '') };
            }
          }
        }
      }

      // Sync UI: redraw the Frame panel's selected color, and the lens type list active state.
      const cp = root.querySelector('[data-color-picker]');
      if (cp) cp.querySelectorAll('[data-color]').forEach(b => b.classList.toggle('is-active', b.dataset.color === state.frameColor));
      root.querySelectorAll('[data-selected-color]').forEach(el => { el.textContent = state.frameColor; });

      // Update the submit button copy to reflect editing
      const submitBtn = form.querySelector('[data-submit]');
      if (submitBtn) submitBtn.innerHTML = 'Update bag — <span data-total>' + dollarsFmt(totalPrice()) + '</span>';

      // Force the flow array to rebuild now that lensTypeId is set
      state.flow = flowForType(state.lensTypeId);
      renderSteps();
      updateTotalsUI();
      gotoStep(0);
      openDrawer();
    }).catch(err => console.error('[lens-configurator] edit prefill', err));
  })();

  function parseEyeProp(str) {
    if (!str) return {};
    const out = {};
    String(str).split('·').forEach(part => {
      const p = part.trim();
      if (p.indexOf('SPH') === 0) out.sph = p.replace('SPH', '').trim().replace('+', '');
      else if (p.indexOf('CYL') === 0) out.cyl = p.replace('CYL', '').trim();
      else if (p.indexOf('Axis') === 0) out.axis = p.replace('Axis', '').replace('°', '').trim();
    });
    if (out.sph && !out.sph.match(/^[+-]?\d/)) out.sph = '0.00';
    return out;
  }

  function range(from, to, step) {
    const out = [];
    for (let v = from; Math.round(v * 100) <= Math.round(to * 100); v += step) out.push(Math.round(v * 100) / 100);
    return out;
  }

  function visibleCartCount(cart) {
    let count = 0;
    (cart.items || []).forEach(it => {
      const props = it.properties || {};
      if (props._bundle_id) {
        if (props._bundle_role === 'frame') count += it.quantity;
      } else {
        count += it.quantity;
      }
    });
    return count;
  }
})();
